import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Save, ChevronDown, ChevronRight, ScrollText, Bus, User as UserIcon,
  Route, Wallet, Check, Clock, ExternalLink, MessageCircle, Link as LinkIcon,
  Trash2, Pencil,
} from "lucide-react";
import { PageHeader } from "@/components/EntityPage";

const DEFAULT_STEPS = () => [
  { label: "Advance", amount: 0, status: "pending", due_date: "", payment_link: "", paid_on: "", txn_id: "" },
  { label: "2nd Payment", amount: 0, status: "pending", due_date: "", payment_link: "", paid_on: "", txn_id: "" },
  { label: "3rd Payment", amount: 0, status: "pending", due_date: "", payment_link: "", paid_on: "", txn_id: "" },
  { label: "Final Payment", amount: 0, status: "pending", due_date: "", payment_link: "", paid_on: "", txn_id: "" },
];

function statusBadge(s) {
  const v = (s || "pending").toLowerCase();
  if (v === "paid") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (v === "active") return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  return "bg-slate-500/10 text-slate-400 border-slate-500/30";
}

function AssignmentEditor({ trip, refs, onSaved }) {
  const [f, setF] = useState({
    ...trip,
    payment_steps: (trip.payment_steps && trip.payment_steps.length ? trip.payment_steps : DEFAULT_STEPS()).slice(0, 4),
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const updStep = (i, k, v) => setF((s) => {
    const ns = [...(s.payment_steps || DEFAULT_STEPS())];
    ns[i] = { ...ns[i], [k]: v };
    return { ...s, payment_steps: ns };
  });

  const totalCost = Number(f.total_trip_cost || 0);
  const totalPaid = (f.payment_steps || []).reduce((a, s) => a + ((s.status || "").toLowerCase() === "paid" ? Number(s.amount || 0) : 0), 0);
  const remaining = Math.max(0, totalCost - totalPaid);
  const stepsTotal = (f.payment_steps || []).reduce((a, s) => a + Number(s.amount || 0), 0);

  async function saveAll() {
    try {
      await api.put(`/trips/${trip.id}`, f);
      toast.success("Assignment saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  }

  async function markPaid(i, paid = true) {
    try {
      await api.put(`/trips/${trip.id}/payment-steps/${i}`, {
        status: paid ? "paid" : "pending",
        payment_link: f.payment_steps[i].payment_link || null,
        amount: Number(f.payment_steps[i].amount || 0),
        due_date: f.payment_steps[i].due_date || null,
        label: f.payment_steps[i].label,
      });
      toast.success(paid ? "Marked paid — remaining balance updated" : "Reverted to pending");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  async function shareLink(i) {
    const s = f.payment_steps[i]; const client = refs.clients.find((c) => c.id === trip.client_id);
    if (!s.payment_link) { toast.error("Add a payment link first, then save"); return; }
    const msg = `Hello ${client?.name || "traveller"}, please complete your ${s.label} of ₹${Number(s.amount || 0).toLocaleString("en-IN")} for trip ${trip.trip_id} via: ${s.payment_link}`;
    const wa = client?.whatsapp || client?.mobile;
    if (wa) window.open(`https://wa.me/${wa.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
    else window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  }

  return (
    <div className="space-y-5 pt-1">
      {/* Client-facing basics */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><Label className="text-[10px] uppercase text-muted-foreground">Assigned vehicle type</Label>
          <Input value={f.assigned_vehicle_type || ""} onChange={(e) => set("assigned_vehicle_type", e.target.value)}
            placeholder="SUV / Tempo Traveller / Bus" data-testid={`ca-veh-type-${trip.id}`}
            className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Vehicle registration no.</Label>
          <Input value={f.assigned_vehicle_reg || ""} onChange={(e) => set("assigned_vehicle_reg", e.target.value)}
            placeholder="WB-01-AB-1234" data-testid={`ca-veh-reg-${trip.id}`}
            className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Total trip cost (₹)</Label>
          <Input type="number" value={f.total_trip_cost || 0} onChange={(e) => set("total_trip_cost", parseFloat(e.target.value) || 0)}
            data-testid={`ca-total-${trip.id}`} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Assigned driver name</Label>
          <Input value={f.assigned_driver_name || ""} onChange={(e) => set("assigned_driver_name", e.target.value)}
            data-testid={`ca-drv-name-${trip.id}`} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Assigned driver mobile</Label>
          <Input value={f.assigned_driver_mobile || ""} onChange={(e) => set("assigned_driver_mobile", e.target.value)}
            data-testid={`ca-drv-mob-${trip.id}`} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">GPS live-track link</Label>
          <Input value={f.gps_link || ""} onChange={(e) => set("gps_link", e.target.value)}
            placeholder="https://maps.google.com/…" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      </div>

      <div><Label className="text-[10px] uppercase text-muted-foreground">Itinerary (shown to client)</Label>
        <textarea rows={3} value={f.itinerary || ""} onChange={(e) => set("itinerary", e.target.value)}
          data-testid={`ca-itin-${trip.id}`}
          placeholder="Day 1: … Day 2: … Day 3: …"
          className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>

      <div><Label className="text-[10px] uppercase text-muted-foreground">Terms & Conditions (client must accept)</Label>
        <textarea rows={4} value={f.terms_conditions || ""} onChange={(e) => set("terms_conditions", e.target.value)}
          data-testid={`ca-tnc-${trip.id}`}
          placeholder="Enter T&C the client must read and accept before viewing trip details…"
          className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>

      {/* Payment plan */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-amber-400"/>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80">4-step payment plan</div>
          </div>
          <div className="text-xs text-muted-foreground">
            Sum of steps <span className="font-mono text-slate-300">{fmt(stepsTotal)}</span> · Paid <span className="font-mono text-emerald-300">{fmt(totalPaid)}</span> · Remaining <span className="font-mono text-amber-300">{fmt(remaining)}</span>
          </div>
        </div>

        <div className="space-y-2">
          {(f.payment_steps || DEFAULT_STEPS()).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 rounded-md border border-border bg-[hsl(224_40%_10%)]">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-muted-foreground">Step {i + 1}</Label>
                <Input value={s.label || ""} onChange={(e) => updStep(i, "label", e.target.value)}
                  className="bg-[hsl(224_40%_12%)] mt-1 h-9 text-sm"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-muted-foreground">Due date</Label>
                <Input type="date" value={s.due_date || ""} onChange={(e) => updStep(i, "due_date", e.target.value)}
                  data-testid={`ca-step-date-${trip.id}-${i}`}
                  className="bg-[hsl(224_40%_12%)] mt-1 h-9 text-sm"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-muted-foreground">Amount (₹)</Label>
                <Input type="number" value={s.amount || 0} onChange={(e) => updStep(i, "amount", parseFloat(e.target.value) || 0)}
                  data-testid={`ca-step-amt-${trip.id}-${i}`}
                  className="bg-[hsl(224_40%_12%)] mt-1 h-9 text-sm"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
                <select value={s.status || "pending"} onChange={(e) => updStep(i, "status", e.target.value)}
                  data-testid={`ca-step-status-${trip.id}-${i}`}
                  className="w-full mt-1 h-9 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="col-span-4">
                <Label className="text-[10px] uppercase text-muted-foreground">Payment link (shared with client)</Label>
                <div className="flex gap-1 mt-1">
                  <Input value={s.payment_link || ""} onChange={(e) => updStep(i, "payment_link", e.target.value)}
                    placeholder="https://rzp.io/l/…"
                    data-testid={`ca-step-link-${trip.id}-${i}`}
                    className="bg-[hsl(224_40%_12%)] h-9 text-sm flex-1"/>
                  <Button type="button" variant="outline" size="icon" title="Share via WhatsApp"
                    onClick={() => shareLink(i)}
                    data-testid={`ca-step-share-${trip.id}-${i}`}
                    className="h-9 w-9 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                    <MessageCircle className="w-3.5 h-3.5"/>
                  </Button>
                </div>
              </div>
              <div className="col-span-12 flex items-center justify-between gap-2 pt-1">
                <div className="text-[11px] text-muted-foreground">
                  {s.status === "paid" ? (
                    <span className="text-emerald-300">✓ Paid {s.paid_on ? `on ${s.paid_on}` : ""} {s.txn_id ? `· ${s.txn_id}` : ""}</span>
                  ) : (
                    <span>Not yet paid</span>
                  )}
                </div>
                {s.status !== "paid" ? (
                  <Button type="button" size="sm" onClick={() => markPaid(i, true)}
                    data-testid={`ca-step-mark-paid-${trip.id}-${i}`}
                    className="bg-emerald-500/90 hover:bg-emerald-500 text-[hsl(224_60%_6%)] font-semibold gap-1 h-8">
                    <Check className="w-3.5 h-3.5"/> Mark paid
                  </Button>
                ) : (
                  <Button type="button" size="sm" variant="outline" onClick={() => markPaid(i, false)}
                    className="border-border h-8">
                    Undo paid
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border/60">
        <Button onClick={saveAll} data-testid={`ca-save-${trip.id}`}
          className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-1">
          <Save className="w-4 h-4"/> Save assignment
        </Button>
      </div>
    </div>
  );
}

function AssignmentCard({ trip, refs, onSaved, onDelete }) {
  const [open, setOpen] = useState(false);
  const client = refs.clients.find((c) => c.id === trip.client_id);
  const accepted = !!trip.client_tnc_accepted;
  const steps = trip.payment_steps || [];
  const paidCount = steps.filter((s) => (s.status || "").toLowerCase() === "paid").length;

  return (
    <Card className="bg-[hsl(224_45%_9%)] border-border" data-testid={`assign-card-${trip.id}`}>
      <div className="px-4 py-3 flex flex-col md:grid md:grid-cols-[auto_1fr_auto] gap-3 md:items-center">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" onClick={() => setOpen(!open)} aria-label={open ? "Collapse" : "Expand"}
            className="text-slate-400 hover:text-white p-1 -m-1 shrink-0">
            {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
          </button>
          <button type="button" onClick={() => setOpen(!open)} className="text-left min-w-0 flex-1 overflow-x-auto -mx-1 px-1 [scrollbar-width:thin]">
            <div className="grid grid-cols-5 gap-3 items-center min-w-[720px] md:min-w-0">
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Trip ID</div>
                <div className="font-mono text-amber-300 text-sm">{trip.trip_id}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Client</div>
                <div className="text-sm truncate">{client?.name || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Dates</div>
                <div className="text-sm whitespace-nowrap">{trip.start_date || "—"} → {trip.end_date || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Total / Remaining</div>
                <div className="text-sm font-mono whitespace-nowrap"><span className="text-sky-300">{fmt(trip.total_trip_cost || 0)}</span> · <span className="text-amber-300">{fmt(trip.remaining_payable || 0)}</span></div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Payments</div>
                <div className="text-sm font-mono text-emerald-300">{paidCount}/4 paid</div>
              </div>
            </div>
          </button>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap md:justify-end shrink-0 border-t border-border/40 md:border-0 pt-2 md:pt-0">
          <Badge className={accepted ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}>
            {accepted ? "T&C accepted" : "Awaiting T&C"}
          </Badge>
          <Button type="button" variant="outline" size="sm" data-testid={`ca-edit-${trip.id}`}
            onClick={() => setOpen(true)}
            className="h-8 border-border hover:border-amber-500/40 hover:text-amber-200 gap-1">
            <Pencil className="w-3.5 h-3.5"/> Edit
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid={`ca-delete-${trip.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete?.(trip.id); }}
            className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10 gap-1">
            <Trash2 className="w-3.5 h-3.5"/> Delete
          </Button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-border/60">
          <AssignmentEditor trip={trip} refs={refs} onSaved={onSaved}/>
        </div>
      )}
    </Card>
  );
}

function NewAssignmentDialog({ refs, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    client_id: "", vehicle_id: "", driver_id: "", start_date: "", end_date: "",
    total_trip_cost: 0, itinerary: "", terms_conditions: "",
    assigned_vehicle_type: "", assigned_vehicle_reg: "",
    assigned_driver_name: "", assigned_driver_mobile: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // auto-fill assigned info when vehicle/driver picked
  useEffect(() => {
    if (f.vehicle_id) {
      const v = refs.vehicles.find((x) => x.id === f.vehicle_id);
      if (v) setF((s) => ({ ...s, assigned_vehicle_type: s.assigned_vehicle_type || v.vehicle_type || "", assigned_vehicle_reg: s.assigned_vehicle_reg || v.registration_no || "" }));
    }
  }, [f.vehicle_id]); // eslint-disable-line
  useEffect(() => {
    if (f.driver_id) {
      const d = refs.drivers.find((x) => x.id === f.driver_id);
      if (d) setF((s) => ({ ...s, assigned_driver_name: s.assigned_driver_name || d.name || "", assigned_driver_mobile: s.assigned_driver_mobile || d.mobile || "" }));
    }
  }, [f.driver_id]); // eslint-disable-line

  async function submit(e) {
    e.preventDefault();
    if (!f.client_id) { toast.error("Choose a client"); return; }
    try {
      await api.post("/trips", { ...f, entry_date: new Date().toISOString().slice(0, 10) });
      toast.success("Assignment created");
      setOpen(false);
      setF({ client_id: "", vehicle_id: "", driver_id: "", start_date: "", end_date: "", total_trip_cost: 0, itinerary: "", terms_conditions: "", assigned_vehicle_type: "", assigned_vehicle_reg: "", assigned_driver_name: "", assigned_driver_mobile: "" });
      onCreated && onCreated();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-assignment-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2">
          <Plus className="w-4 h-4"/> New assignment
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-3xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">New client assignment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Client *</Label>
              <Select value={f.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="new-assign-client" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Pick a client"/></SelectTrigger>
                <SelectContent>{refs.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.mobile ? ` · ${c.mobile}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Vehicle</Label>
              <Select value={f.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Pick a vehicle"/></SelectTrigger>
                <SelectContent>{refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no} · {v.vehicle_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Driver</Label>
              <Select value={f.driver_id} onValueChange={(v) => set("driver_id", v)}>
                <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Pick a driver"/></SelectTrigger>
                <SelectContent>{refs.drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Total trip cost (₹)</Label>
              <Input type="number" value={f.total_trip_cost} onChange={(e) => set("total_trip_cost", parseFloat(e.target.value) || 0)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Start date</Label>
              <Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">End date</Label>
              <Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Assigned vehicle type</Label>
              <Input value={f.assigned_vehicle_type} onChange={(e) => set("assigned_vehicle_type", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Vehicle registration</Label>
              <Input value={f.assigned_vehicle_reg} onChange={(e) => set("assigned_vehicle_reg", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Driver name (shown to client)</Label>
              <Input value={f.assigned_driver_name} onChange={(e) => set("assigned_driver_name", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Driver mobile (shown to client)</Label>
              <Input value={f.assigned_driver_mobile} onChange={(e) => set("assigned_driver_mobile", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/>
            </div>
          </div>
          <div><Label className="text-[10px] uppercase text-muted-foreground">Itinerary</Label>
            <textarea rows={3} value={f.itinerary} onChange={(e) => set("itinerary", e.target.value)}
              className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>
          <div><Label className="text-[10px] uppercase text-muted-foreground">Terms & Conditions</Label>
            <textarea rows={4} value={f.terms_conditions} onChange={(e) => set("terms_conditions", e.target.value)}
              className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>
          <div className="text-[11px] text-muted-foreground">A 4-step payment plan (Advance / 2nd / 3rd / Final) will be initialised automatically — set the amounts and dates after creation.</div>
          <div className="flex justify-end pt-1">
            <Button type="submit" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Create</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientAssignments() {
  const confirm = useConfirm();
  const [trips, setTrips] = useState([]);
  const [refs, setRefs] = useState({ vehicles: [], drivers: [], clients: [] });
  const [clientFilter, setClientFilter] = useState("all");
  const [tncFilter, setTncFilter] = useState("all");
  const [phoneFilter, setPhoneFilter] = useState("");

  async function load() {
    try {
      const [t, v, d, c] = await Promise.all([
        api.get("/trips"), api.get("/vehicles"), api.get("/drivers"), api.get("/clients"),
      ]);
      setTrips(t.data);
      setRefs({ vehicles: v.data, drivers: d.data, clients: c.data });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Load failed"); }
  }
  useEffect(() => { load(); }, []);

  async function delAssignment(tripId) {
    const t = trips.find((x) => x.id === tripId);
    const ok = await confirm({
      title: "Delete assignment?",
      description: `Are you sure you want to delete ${t?.trip_id || "this trip"}? Non-admins will submit a request for admin approval. Admins can restore from Trash for 30 days.`,
      confirmText: "Yes, delete",
    });
    if (!ok) return;
    try {
      const res = await api.delete(`/trips/${tripId}`);
      if (res.data?.trashed) {
        toast("Assignment deleted", {
          duration: 6000,
          action: {
            label: "Restore",
            onClick: async () => {
              const trash = (await api.get("/trash")).data || [];
              const item = trash.find((x) => x.original_id === tripId);
              if (item) { await api.post(`/trash/${item.id}/restore`); toast.success("Restored"); load(); }
            },
          },
        });
      } else {
        toast.success("Assignment deleted");
      }
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 202 || (detail && typeof detail === "object" && detail.queued)) {
        // Non-admin — offer WhatsApp notify link to admin
        try {
          const { data: admin } = await api.get("/admin-contact");
          const wa = (admin.mobile || "").replace(/[^0-9]/g, "");
          const msg = encodeURIComponent(`Hi ${admin.name || "Admin"}, please review my delete request for trip ${t?.trip_id || tripId} in Ashok NE Travels.`);
          toast("Delete request submitted for admin approval", {
            duration: 8000,
            action: wa ? { label: "Notify admin", onClick: () => window.open(`https://wa.me/${wa}?text=${msg}`, "_blank") } : undefined,
          });
        } catch { toast.success("Delete request submitted for admin approval"); }
      } else {
        toast.error(formatErr(detail) || "Delete failed");
      }
    }
  }

  const filtered = useMemo(() => trips.filter((t) => {
    if (clientFilter !== "all" && t.client_id !== clientFilter) return false;
    if (tncFilter === "accepted" && !t.client_tnc_accepted) return false;
    if (tncFilter === "pending" && t.client_tnc_accepted) return false;
    if (phoneFilter.trim()) {
      const digits = phoneFilter.replace(/[^0-9]/g, "");
      const client = refs.clients.find((c) => c.id === t.client_id);
      const contactDigits = [(client?.mobile || ""), (client?.whatsapp || ""), (t.assigned_driver_mobile || "")]
        .join(" ").replace(/[^0-9]/g, "");
      if (!contactDigits.includes(digits)) return false;
    }
    return true;
  }), [trips, clientFilter, tncFilter, phoneFilter, refs.clients]);

  const summary = useMemo(() => {
    const totalCost = filtered.reduce((a, t) => a + Number(t.total_trip_cost || 0), 0);
    const totalPaid = filtered.reduce((a, t) => a + Number(t.total_paid || 0), 0);
    const totalDue = filtered.reduce((a, t) => a + Number(t.remaining_payable || 0), 0);
    const accepted = filtered.filter((t) => t.client_tnc_accepted).length;
    return { totalCost, totalPaid, totalDue, accepted, count: filtered.length };
  }, [filtered]);

  return (
    <div>
      <PageHeader
        title="Client Assignments"
        subtitle="Enter every detail the client will see — assigned vehicle, driver, itinerary, T&C, total trip cost and the 4-step payment plan. Mark each step paid as payments arrive."
        right={<NewAssignmentDialog refs={refs} onCreated={load}/>}
      />

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[10px] uppercase text-muted-foreground">Assignments</div>
          <div className="text-2xl font-mono mt-1">{summary.count}</div>
          <div className="text-[11px] text-emerald-300 mt-1">{summary.accepted} T&C accepted</div>
        </Card>
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[10px] uppercase text-muted-foreground">Total value</div>
          <div className="text-2xl font-mono text-sky-300 mt-1">{fmt(summary.totalCost)}</div>
        </Card>
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[10px] uppercase text-muted-foreground">Total received</div>
          <div className="text-2xl font-mono text-emerald-300 mt-1">{fmt(summary.totalPaid)}</div>
        </Card>
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[10px] uppercase text-muted-foreground">Balance due</div>
          <div className="text-2xl font-mono text-amber-300 mt-1">{fmt(summary.totalDue)}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[hsl(224_45%_9%)] border-border p-3 flex flex-wrap items-center gap-3 mb-4">
        <div className="text-[10px] uppercase text-muted-foreground">Client</div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger data-testid="ca-filter-client" className="w-[220px] h-8 bg-[hsl(224_40%_12%)] text-sm"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {refs.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[10px] uppercase text-muted-foreground">T&C</div>
        <Select value={tncFilter} onValueChange={setTncFilter}>
          <SelectTrigger data-testid="ca-filter-tnc" className="w-[160px] h-8 bg-[hsl(224_40%_12%)] text-sm"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="pending">Awaiting</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-[10px] uppercase text-muted-foreground">Phone</div>
        <Input
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
          placeholder="Search by mobile / WhatsApp / driver number"
          data-testid="ca-filter-phone"
          className="w-[280px] h-8 bg-[hsl(224_40%_12%)] text-sm font-mono"/>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} of {trips.length}</div>
      </Card>

      {/* Cards */}
      {filtered.length === 0 ? (
        <Card className="p-10 bg-[hsl(224_45%_9%)] border-border text-center text-muted-foreground">No assignments match the filter</Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => <AssignmentCard key={t.id} trip={t} refs={refs} onSaved={load} onDelete={delAssignment}/>)}
        </div>
      )}
    </div>
  );
}
